import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as Highcharts from 'highcharts';

import { AppTeamUtil } from '../../core/app-team-util';
import { CategoryItem } from '../../api/categories/category-item.model';
import { DeviceManagerService } from '../../core/services/device-manager.service';
import { MemberCountWithCategoriesResult } from '../../api/member/member-count-with-categories-result.model';
import { MemberManagerService } from '../../api/member/member-manager.service';
import { MyCampApp } from '../../api/common/enumerations';
import { Organization } from '../../api/organization/organization.model';
import { OrganizationManagerService } from '../../api/organization/organization-manager.service';
import { ThemeManagerService } from '../../api/theme/theme-manager.service';
import CategoryItemType = MyCampApp.CategoryItemType;
import { ChildrenCountWithCategoriesResult } from '../../api/children/children-count-with-categories-result.model';
import { ChildrenManagerService } from '../../api/children/children-manager.service';

@Component({
	selector: 'mycampapp-select-categories-dialog',
	templateUrl: './select-categories-dialog.component.html',
	styleUrls: ['./select-categories-dialog.component.less']
})
export class SelectCategoriesDialogComponent implements OnInit
{
	@Input() title = 'Select Categories';
	@Input() allowSelectAll = false;
	@Input() childrenMode = false;
	@Input() disabledCategoryIds: number[] = [];
	@Input() memberTypeId?: number;
	@Input() noCategoriesMeansAllUsers = true;
	@Input() selectedCategoryIds: number[] = [];
	@Input() showUserCount = true;

	@Output() categoriesSelected = new EventEmitter<number[] | undefined>();

	orgCategoriesByType: Map<MyCampApp.CategoryItemType, CategoryItem[]> = new Map<MyCampApp.CategoryItemType, CategoryItem[]>();
	orgCategoryItemTypes: CategoryItemType[] = [];
	childrenCountResult?: ChildrenCountWithCategoriesResult;
	memberCountResult?: MemberCountWithCategoriesResult;
	currentCategoryType?: MyCampApp.CategoryItemType;
	loading = false;
	selectedCategories: CategoryItem[] = [];
	selectedCategoriesByType: Map<MyCampApp.CategoryItemType, CategoryItem[]> = new Map<MyCampApp.CategoryItemType, CategoryItem[]>();
	selectAll = false;
	usedCategoryTypes: any[] = [];
	visible = true;
	pieChart?: Highcharts.Options;

	private organization?: Organization;
	private chart?: Highcharts.Chart;

	Highcharts = Highcharts;
	Math = Math;
	MyCampApp = MyCampApp;

	constructor(public deviceManager: DeviceManagerService,
				private childrenManager: ChildrenManagerService,
				private memberManager: MemberManagerService,
	            private organizationManager: OrganizationManagerService,
	            private themeManager: ThemeManagerService) { }

	ngOnInit(): void
	{
		this.organizationManager.currentOrganization.subscribe(organization =>
		{
			this.organization = organization;
			let orgCategoryItems = organization.getCategoriesForMembershipType(this.memberTypeId)
				.sort((c1, c2) => MyCampApp.getCategoryItemTypeString(c1.CategoryItemType).localeCompare(MyCampApp.getCategoryItemTypeString(c2.CategoryItemType)));
			this.orgCategoriesByType = AppTeamUtil.groupBy(orgCategoryItems, (ci: CategoryItem) => ci.CategoryItemType);
			for (const categoryType of this.orgCategoriesByType.keys())
			{
				this.orgCategoryItemTypes.push(categoryType);
				const orgCategoriesForType = this.orgCategoriesByType.get(categoryType)!;
				const selectedCategoriesForType = orgCategoriesForType.filter((cat: CategoryItem) => this.selectedCategoryIds.some(id => cat.Id === id));
				this.selectedCategoriesByType.set(categoryType, selectedCategoriesForType);
			}
			this.orgCategoryItemTypes.sort((cat1, cat2) => MyCampApp.isCategoryItemTypeSortedBefore(cat1, cat2));
		});

		// this.deviceManager.windowResize.subscribe(() =>         setTimeout(() => this.chart.refresh(), 100));

		this.updateUserCount();
	}

	getCategoryItemTypeIcon(type: MyCampApp.CategoryItemType): IconProp
	{
		switch (type)
		{
			case MyCampApp.CategoryItemType.Bus: return 'bus-alt';
			case MyCampApp.CategoryItemType.Group: return 'user-friends';
			case MyCampApp.CategoryItemType.Other: return 'tags';
			case MyCampApp.CategoryItemType.Session: return 'calendar-week';
			case MyCampApp.CategoryItemType.Location: return 'map-marked-alt';
		}
	}

	getCategoryName(item: CategoryItem): string
	{
		if (this.organization != undefined && this.organization.InEnrollmentPeriod) return item.calculatedName;
		return item.Name;
	}

	getSelectedCategoriesByType(categoryType: MyCampApp.CategoryItemType): CategoryItem[]
	{
		return this.selectedCategoriesByType.get(categoryType)!;
	}

	isCategoryUsed(categoryType: MyCampApp.CategoryItemType)
	{
		return this.usedCategoryTypes.some(x => x.categoryType === categoryType);
	}

	onCategoryTypeClicked(type: CategoryItemType): void
	{
		this.currentCategoryType = type;
		this.selectedCategories = this.selectedCategoriesByType.get(this.currentCategoryType)!;
		this.selectAll = this.selectedCategoriesByType.get(this.currentCategoryType!)!.length === this.orgCategoriesByType.get(this.currentCategoryType!)!.length;
		if (this.chart) setTimeout(() => this.chart!.reflow(), 50);
	}

	onSelectedCategoriesChanged()
	{
		this.selectedCategoriesByType.set(this.currentCategoryType!, this.selectedCategories);
		this.selectAll = this.selectedCategoriesByType.get(this.currentCategoryType!)!.length === this.orgCategoriesByType.get(this.currentCategoryType!)!.length;
		this.updateUserCount();
		// setTimeout(() => this.dialog.onWindowResize(), 100);
	}

	onDeleteSelectedCategory(selectedCategory: CategoryItem)
	{
		this.selectedCategoriesByType.set(selectedCategory.CategoryItemType,
			this.selectedCategoriesByType.get(selectedCategory.CategoryItemType)!.filter(cat => cat.Id !== selectedCategory.Id));
		this.updateUserCount();
	}

	onSelectAllChanged(checked: boolean)
	{
		if (this.orgCategoryItemTypes === undefined || this.currentCategoryType === undefined) return;
		if (checked)
			this.selectedCategoriesByType.set(this.currentCategoryType, this.orgCategoriesByType.get(this.currentCategoryType)!);
		else
			this.selectedCategoriesByType.set(this.currentCategoryType, this.orgCategoriesByType.get(this.currentCategoryType)!
				.filter(ci => this.disabledCategoryIds.some(id => id === ci.Id)));
		this.selectedCategories = this.selectedCategoriesByType.get(this.currentCategoryType)!;
		this.updateUserCount();
	}

	onOk(): void
	{
		this.categoriesSelected.emit(this.currentSelectedCategoryIds); // this.selectedAlbumNode === undefined ? null : this.selectedAlbumNode.data);
	}

	onCancel(): void
	{
		this.categoriesSelected.emit(undefined);
	}

	chartCallback(chart: any) {
		this.chart = chart;
		setTimeout(() => chart.reflow(), 100); // Because Highcharts does not always size them correctly right away
	}

	private get currentSelectedCategoryIds(): number[]
	{
		let selectedCategories: CategoryItem[] = [];
		for (const categoryType of this.selectedCategoriesByType.keys())
		{
			selectedCategories = selectedCategories.concat(this.selectedCategoriesByType.get(categoryType)!);
		}
		return selectedCategories.map(cat => cat.Id);
	}

	get selectedUserCount(): number
	{
		if (this.childrenMode)
			return (this.noCategoriesMeansAllUsers || this.currentSelectedCategoryIds.length > 0) ? this.childrenCountResult!.MatchingChildIds.length : 0

		return (this.noCategoriesMeansAllUsers || this.currentSelectedCategoryIds.length > 0) ? this.memberCountResult!.MembersMatchingCategories : 0
	}

	get totalUserCount(): number
	{
		if (this.childrenMode)
			return (this.noCategoriesMeansAllUsers || this.currentSelectedCategoryIds.length > 0) ? this.childrenCountResult!.TotalChildren : 0

		return (this.noCategoriesMeansAllUsers || this.currentSelectedCategoryIds.length > 0) ? this.memberCountResult!.TotalMembers : 0
	}

	// private showCategory(type: MyCampApp.CategoryItemType)
	// {
	// 	const categories = this.allCategoryTypes.find(list => list[0].CategoryItemType === type);
	// 	this.usedCategoryTypes.push({ categoryType: type, categories: categories });
	// }

	private updateUserCount()
	{
		setTimeout(() =>
		{
			const api: any = this.childrenMode ? this.childrenManager.getChildrenCountForCategories(this.currentSelectedCategoryIds)
				: this.memberManager.getMemberCountForCategories(this.currentSelectedCategoryIds);
			api.subscribe((result: MemberCountWithCategoriesResult | ChildrenCountWithCategoriesResult) =>
			{
				const total = this.childrenMode ? (<ChildrenCountWithCategoriesResult>result).TotalChildren
					: (<MemberCountWithCategoriesResult>result).TotalMembers;
				const matchCount = this.childrenMode ? (<ChildrenCountWithCategoriesResult>result).MatchingChildIds.length
					: (<MemberCountWithCategoriesResult>result).MembersMatchingCategories;
				setTimeout(() =>
				{
					this.pieChart = {
						chart: { type: 'pie' },
						colors: [this.themeManager.NEUTRAL_THEME_DARK_BACKGROUND_COLOR!, this.themeManager.NEUTRAL_THEME_LIGHT_BACKGROUND_COLOR!],
						credits: { enabled: false },
						legend: { enabled: true },
						plotOptions: { pie: { dataLabels: { enabled: false }, showInLegend: true } },
						title: undefined,
						series: [
							{
								name: 'Users',
								type: 'pie',
								data: [
									{ name: 'Selected', y: this.selectedUserCount },
									{ name: 'Not Selected', y: total - matchCount }
								]
							}
						]
					};
				}, 100);
				if (this.childrenMode)
					this.childrenCountResult = <ChildrenCountWithCategoriesResult>result;
				else
					this.memberCountResult = <MemberCountWithCategoriesResult>result;
			});
		}, 200);
	}

	isDisabledCategoryItem(categoryItem: CategoryItem): boolean
	{
		if (this.currentCategoryType == null) return false;

		// Disable the category item if any category items with the same category type are in the list of disabled category ids
		return this.orgCategoriesByType.get(categoryItem.CategoryItemType)!.some(ci => this.disabledCategoryIds.some(id => id === ci.Id));
	}
}
